import { validateRequest } from '@oscar/request';
import { Pagination, Table, TableProps } from '@oscar/theme';
import { useDeepCompareEffect } from '@oscar/utils';
import { isArray, merge, mergeWith } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTable from './hooks/useTable';
import {
  fetchData as requestData,
  initializeValues,
  preloadData,
  submitData,
} from './reducers';
import { selectorFactory } from './selectors';
import './style.scss';
import { TableContext, TableContextProps } from './tableContext';
import { Request, RequestParams, TableOptions, TableRef } from './typed';

function correctTableName(name: string) {
  return name.replace(' ', '_');
}

const getConfig = ({
  mode = 'LOCAL',
  name,
  keyField = 'id',
  reducerName = 'table',
  pageSize = 10,
  params,
  ...options
}: TableOptions): TableOptions => {
  let defautlParams;
  if (mode === 'REMOTE') {
    defautlParams = {
      ...(params || {}),
      pagination: {
        pageSize,
        pageIndex: 1,
        ...(params?.pagination || {}),
      },
    };
  }
  return {
    ...options,
    mode,
    name: correctTableName(name),
    keyField,
    reducerName,
    pageSize,
    params: defautlParams,
  };
};

export default function withTable<T>(config: TableOptions) {
  const options = getConfig(config);
  const { name, pageSize, keyField, reducerName, mode } = options;
  const selectors = selectorFactory(name, keyField, reducerName);
  const TableHOC: React.FC<TableProps<object> & { table?: TableRef } & T> = (
    props
  ) => {
    const dataSource = useSelector(selectors.selectAll);
    const metadata = useSelector(selectors.getMetadata);
    const isLoading = useSelector(selectors.isLoading);
    const dispatch = useDispatch();
    const [wrapTable] = useTable(props?.table);

    useEffect(() => {
      dispatch(
        initializeValues({
          metadata: {
            table: name,
            key: keyField,
            mode,
          },
          options: {
            name,
            keyField,
            pageSize,
          },
        })
      );
    }, []);

    const customizer = (objValue, srcValue) => {
      if (isArray(objValue)) {
        return srcValue;
      }
    };

    const fetchData = useCallback(
      (params?: RequestParams) => {
        const nextParams = mergeWith({}, metadata, params || {}, customizer);
        if (nextParams.pagination) {
          const { pageSize, pageIndex, current } = nextParams.pagination;
          nextParams.pagination = {
            pageSize,
            pageIndex: current || pageIndex,
          };
        }
        if (options.request) {
          dispatch(
            requestData.action({
              metadata: {
                table: name,
                key: keyField,
                mode,
              },
              request: validateRequest(
                {
                  ...options.request,
                  params: nextParams,
                },
                { ...props, dataSource, metadata }
              ),
            })
          );
        }
      },
      [mode, options.request, metadata]
    );

    const reloadData = useCallback(
      (params?: RequestParams) => {
        const nextParams = mergeWith(
          {},
          { pagination: { pageSize: options.pageSize, pageIndex: 0 } },
          params || {},
          customizer
        );
        if (options.request) {
          dispatch(
            requestData.action({
              metadata: {
                table: name,
                key: keyField,
                mode,
              },
              request: validateRequest(
                {
                  ...options.request,
                  params: nextParams,
                },
                { ...props, dataSource, metadata }
              ),
            })
          );
        }
      },
      [mode, options.request]
    );

    useDeepCompareEffect(() => {
      if (options.request) {
        fetchData(options.params);
      }
    }, []);

    useDeepCompareEffect(() => {
      if (options.preload) {
        dispatch(
          preloadData.action(
            options.preload.map((request) => ({
              metadata: {
                table: name,
                key: options.keyField,
                mode,
              },
              request: validateRequest(request, {
                ...props,
                dataSource,
                metadata,
              }),
            }))
          )
        );
      }
    }, [options.preload]);

    const handleTableChange = useCallback(
      (pagination, filters, sorter) => {
        fetchData({ pagination, filters, sorter });
      },
      [mode, options.request, metadata]
    );

    const onPageChange = useCallback(
      (pageIndex, pageSize) => {
        fetchData({
          pagination: merge(metadata.pagination, { pageIndex, pageSize }),
        });
      },
      [mode, options.request, metadata]
    );

    const remoteProps = useMemo(() => {
      let remoteFn = {};
      if (mode === 'REMOTE') {
        remoteFn = {
          onChange: handleTableChange,
        };
      }

      const data = {
        ...remoteFn,
        ...metadata,
      };
      if (wrapTable) {
        wrapTable.selectors = selectors;
        wrapTable.options = options;
      }

      return data;
    }, [mode, metadata]);

    const doAction = useCallback((request: Request, data: any) => {
      dispatch(
        submitData.action({
          metadata: {
            table: name,
            key: keyField,
            mode,
          },
          request: validateRequest(request, data),
        })
      );
    }, []);

    const getMetadata = useCallback(() => {
      return metadata;
    }, [metadata]);

    const tableContextValue = useMemo<TableContextProps>(
      () => ({
        options,
        doAction,
        fetchData,
        getMetadata,
        reloadData,
      }),
      [options, metadata]
    );

    useDeepCompareEffect(() => {
      if (wrapTable) {
        wrapTable.methods = {
          fetchData,
          getMetadata,
          reloadData,
        };
      }
    }, [metadata]);

    const columnConfig = useMemo(() => {
      if (props?.columns) {
        return props?.columns;
      }
      if (config?.columns) {
        if (metadata.filters || metadata?.sorter) {
          return config?.columns.map((item) => {
            const result = { ...item };
            if (item?.key && metadata.filters && metadata.filters[item.key]) {
              if (result.filteredValue instanceof Function) {
                result.filteredValue =
                  result.filteredValue(metadata.filters[item.key]) || null;
              } else {
                result.filteredValue = metadata.filters[item.key] || null;
              }
            }
            if (
              item?.key &&
              metadata.sorter &&
              metadata.sorter.field === item.key
            ) {
              result.sortOrder = metadata.sorter.order;
            }
            return result;
          });
        }
        return config.columns;
      }
      return undefined;
    }, [config?.columns, props?.columns, metadata]);

    return (
      <TableContext.Provider value={tableContextValue}>
        <div className="core-table">
          <Table
            {...props}
            rowKey={keyField}
            dataSource={dataSource}
            loading={isLoading}
            {...remoteProps}
            pagination={false}
            columns={columnConfig}
          />
          <br />
          <div className="pagination">
            <Pagination
              showSizeChanger
              {...{
                ...remoteProps?.pagination,
                defaultCurrent: 1,
                current: remoteProps?.pagination?.pageIndex,
              }}
              onChange={onPageChange}
            />
          </div>
        </div>
      </TableContext.Provider>
    );
  };

  return TableHOC;
}
